import React from 'react'
import { Button, Empty } from 'antd'
import { format, isAfter, isBefore, isEqual, isWithinInterval, startOfDay } from 'date-fns'
import { Link, useParams } from 'react-router-dom'
import {
  ConfigQuestionniareGroup,
  SubjectAssignmentsType,
  useSubjectAssignments,
} from '../../hooks/useSubjectAssignments'
import { ListItemContent } from '../../components/display/ListItemContent'
import { AssignmentType, SUBJECT_STATUS_ENUM } from '../../Types'
import { analytics } from '../../util/analytics'

import { useProjectById } from '../../hooks/useProjectById'
import { getPrioritisedLanguage } from '../../util/languages'
import { ActivityOverview, ASSIGNMENT_STATUS } from './AssignmentActivityOverview'
import { InfoCircleOutlined } from '@ant-design/icons'

const getLangKey = (prioritisedLanguageIsoCode, lookupObj, subjectLanguageIsoCode) => {
  return prioritisedLanguageIsoCode && lookupObj[prioritisedLanguageIsoCode]
    ? prioritisedLanguageIsoCode
    : subjectLanguageIsoCode.toLowerCase()
}

const computeStatus = (progress: number) => {
  if (progress === 100) {
    return ASSIGNMENT_STATUS.COMPLETED
  }

  if (progress === 0) {
    return ASSIGNMENT_STATUS.MISSED
  }

  return ASSIGNMENT_STATUS.PARTIAL
}

const windowClosed = (siteTime: Date, endDate: Date, subjectStatus, progress) => {
  if (subjectStatus !== SUBJECT_STATUS_ENUM.ACTIVE) {
    return subjectStatus
  }

  if (progress === 100) {
    return ASSIGNMENT_STATUS.COMPLETED
  }

  return isAfter(new Date(siteTime), new Date(endDate)) ? ASSIGNMENT_STATUS.MISSED : ASSIGNMENT_STATUS.PENDING
}

type Props = {
  subjectLanguageIsoCode: string
  subjectStatus: SUBJECT_STATUS_ENUM
}

/**
 * Truncate the date map by date.
 * Used to create the calendar date map in case of an endDate present in config
 *
 * @param dateMap
 * @param endDate
 * @return {object}
 */
const truncateCalendarMapByDate = (dateMap: any[], endDate: Date): { [key: string]: object } => {
  // Truncate by this date
  const cutoffDateStartOfDay = startOfDay(endDate)

  // Filter for assignments date (day) are before or the same day as the endDate
  return Object.entries(dateMap).reduce((acc: {}, [key, value]: [string, any]) => {
    const itemDateStartOfDay = startOfDay(new Date(key))
    const isSameOrBefore =
      isBefore(itemDateStartOfDay, cutoffDateStartOfDay) || isEqual(itemDateStartOfDay, cutoffDateStartOfDay)

    // Before or same day as endDate
    if (isSameOrBefore) {
      acc[key] = value
    }
    return acc
  }, {})
}

/**
 * Determines if an assignment should be skipped
 * Condition defined by the termination date and the progress of tasks.
 *
 * @param assignmentDate
 * @param endDate
 * @param tasks
 * @returns {boolean}
 */
const skipAssignment = (assignmentDate: Date, endDate: Date, tasks: any[]): boolean => {
  const keyDateStartOfDay = startOfDay(assignmentDate)
  const endDateStartOfDay = startOfDay(endDate)

  // If the date is AFTER the termination date, check if there are tasks in progress
  if (isAfter(keyDateStartOfDay, endDateStartOfDay)) {
    // If any of the the tasks are hvign progess we DO NOT skip. Hence the negation
    return !tasks.some((task) => task.progress > 0)
  }

  // Default is that we show the assignment
  return false
}

/**
 *
 * @param subjectLanguageIsoCode
 * @param subjectStatus
 * @constructor
 *
 */
export const SubjectAssignments = ({ subjectLanguageIsoCode, subjectStatus }: Props) => {
  const { projectId, subjectId } = useParams() as { projectId: string; subjectId: string }

  const dateComparisonStringFormat: string = 'yyyy/M/dd'

  const { data } = useSubjectAssignments(projectId, subjectId)
  const todayStrDate = format(new Date(), dateComparisonStringFormat)

  const hasContent = data ? Object.keys(data).length > 0 : 0

  const { data: projectData } = useProjectById(projectId)

  // If one of the prioritised languages is present in the project, use that
  // - It will be checked on task level if a translation for this language exist
  const prioritisedLanguageIsoCode = getPrioritisedLanguage(projectData.languages)

  const isNewDataStructure = data?.config ? true : false

  const config = data?.config
  const dataArray = isNewDataStructure ? data.dateMap : data

  const endDate: Date | null = config.endDate ? new Date(config.endDate) : null
  const endStrDate: string | null = endDate ? format(endDate, dateComparisonStringFormat) : null

  /**
   *
   * If the subject has not been fully onboarded, we do not have any data
   * If the subject in in the CREATED state, show this message
   */
  const dataArrayKeys = Object.keys(dataArray)
  const first = dataArrayKeys[0] && isNewDataStructure ? dataArrayKeys[0] : null

  if (!first) {
    return (
      <div className="col-8 p-5 mt-3 bg-white rounded-3 border d-flex flex-column justify-content-center align-items-center text-center mx-auto">
        <div className="mt-4">
          <InfoCircleOutlined style={{ color: '#dc3545', fontSize: 42 }} />
        </div>
        <h4 className="mt-3">Participant Onboarding Incomplete</h4>
        <p className="mt-3">
          The participant has been created, but a device has not been assigned,
          <br />
          resulting in an incomplete onboarding process.
        </p>
        <p className="mt-3">Please assign a device to the participant to complete onboarding.</p>
        <Link className="my-4" to={`../onboard/${subjectId}`}>
          <Button type="primary" className="my-2 w-100">
            Assign Device
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <>
      {isNewDataStructure && data.config.dailyQuestionnaires.length > 0 ? (
        <div className="col-12 mt-3 py-1 mb-3">
          <p className="p-small fw-bolder">DAILY ASSIGNMENTS</p>
        </div>
      ) : null}

      {isNewDataStructure && data.config.dailyQuestionnaires.length > 0
        ? config.dailyQuestionnaires.map((questionnaireTypeGroup: ConfigQuestionniareGroup[], index) => {
            const name = getLangKey(
              prioritisedLanguageIsoCode,
              questionnaireTypeGroup[0].questionnaire.name,
              subjectLanguageIsoCode
            )

            // Create the calendar date map
            // Depending on if an endDate is present in config
            // - If there is an endDate, truncate the map to the endDate being the last day of the calendar
            // - Otherwise use the original date map
            const calendarDateMap = endDate ? truncateCalendarMapByDate(data.dateMap, endDate) : data.dateMap

            return (
              <ActivityOverview
                key={index}
                questionnaireTypeGroup={questionnaireTypeGroup}
                data={calendarDateMap}
                config={config}
                langKey={name}
              ></ActivityOverview>
            )
          })
        : null}

      <div className="col-12 py-1 mb-3 mt-3">
        <p className="p-small fw-bolder">ALL ASSIGNMENTS</p>
      </div>

      {Object.keys(dataArray).map((dateKey, index) => {
        //
        // handle breaking change in backend
        // before we sent a raw array - we updated to send an obj: {assignments: [], daté: Date, missed: boolean}
        //
        const itemData = dataArray[dateKey]

        const tasks = isNewDataStructure ? itemData.assignments : itemData

        const d = new Date(dateKey)
        const startDate = format(d, 'dd MMM yyyy')
        const day = format(d, 'iii')

        /**
         *
         * Displaying assignments after a termination date is depending on
         * - An endDate present in config
         * - The assignment after termination date has a progression > 0
         *
         * "displayAssignment" defines if an assignment is displayed
         * - can only be false if the assignment date is after a termination date (config.endDate)
         *
         * "displayEndComponent" defines if the EndComponent is shown.
         * - can only be true if the assignment date is the same as the termination date (config.endDate)
         *
         */

        const itemStrDate: string = format(new Date(itemData.date), dateComparisonStringFormat)
        const isSkipped: boolean = endDate ? skipAssignment(d, endDate, tasks) : false
        const displayEndComponent: boolean = endDate ? itemStrDate === endStrDate : false

        if (isSkipped) {
          return null
        }

        return (
          <div key={index} className="col-12 mb-1">
            <div className="" style={{ minWidth: 100 }}>
              <div className="mt-2 rounded-5 py-1 px-2 mb-2">
                <p className="p-small fw-bold" style={{ color: '#095ada' }}>
                  {day} {startDate}
                </p>
              </div>
            </div>

            <div className="w-100">
              {tasks.length > 0 &&
                tasks.map((task: AssignmentType & { protocolTitle: string }, taskIndex) => {
                  // Language key. We need to check on question level fi a translation exists
                  // - if the question does not contain a translation for the default language in the title, it is not present
                  // - use the subject's language
                  const langKey = getLangKey(prioritisedLanguageIsoCode, task.name, subjectLanguageIsoCode)
                  const name = task.name[langKey]

                  //
                  // calculate the task status
                  // Take into account if this is the current day
                  //
                  const taskStatus =
                    itemData.date === todayStrDate
                      ? windowClosed(data?.config.siteTime, new Date(task?.endDate), subjectStatus, task.progress)
                      : computeStatus(task.progress)

                  return (
                    <div
                      key={taskIndex}
                      className="w-100 bg-white d-flex align-items-center py-1 px-3  "
                      style={{
                        borderBottom: '1px solid #e8e8e8',
                        borderLeft: taskStatus === ASSIGNMENT_STATUS.MISSED ? '2px solid #dc3545' : '',
                      }}
                    >
                      <ListItemContent
                        label="Title:"
                        title={name}
                        capitalizeText={true}
                        style={{ width: '30%' }}
                      ></ListItemContent>

                      <ListItemContent
                        label="Protocol:"
                        title={task?.protocolTitle || '-'}
                        capitalizeText={true}
                        style={{ width: '25%' }}
                      ></ListItemContent>

                      <ListItemContent
                        label="Progress:"
                        title={task.progress.toString() + '%'}
                        style={{ width: '15%' }}
                      ></ListItemContent>

                      <ListItemContent
                        label="Status:"
                        type={task.progress === 0 && taskStatus === ASSIGNMENT_STATUS.MISSED ? 'danger' : ''}
                        title={taskStatus}
                        style={{ width: '20%' }}
                        capitalizeText={true}
                      ></ListItemContent>

                      <div className="d-flex justify-content-end" style={{ width: '10%' }}>
                        <Link
                          onClick={() => {
                            analytics.track('VIEW_ASSIGNMENT_ANSWERS')
                          }}
                          to={'assignment/' + task._id}
                          className="px-3"
                        >
                          <Button type="primary" className="">
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )
                })}

              {tasks.length === 0 && (
                <MissedAssignment
                  itemData={itemData}
                  configQuestionnaires={config?.dailyQuestionnaires}
                  langKey={prioritisedLanguageIsoCode}
                ></MissedAssignment>
              )}
            </div>
            {displayEndComponent && (
              <div className="mt-6">
                <EndComponent endDate={config.endDate} subjectStatus={config.subjectStatus} />
              </div>
            )}
          </div>
        )
      })}

      {!hasContent ? <Empty description={'Participant has no recorded answers yet '}></Empty> : null}
    </>
  )
}

type MissedAssignmentProps = {
  langKey: string | undefined
  status?: string
  itemData?: any // This should be more specific based on your data structure
  configQuestionnaires: SubjectAssignmentsType['config']['dailyQuestionnaires']
}
const MissedAssignment = ({
  langKey,
  status = ASSIGNMENT_STATUS.MISSED,
  itemData,
  configQuestionnaires,
}: MissedAssignmentProps) => {
  return (
    <>
      {configQuestionnaires.map((questionnaireWithExtra, index) => {
        const curTime = new Date()
        const daysDate = new Date(itemData.date)
        daysDate.setHours(curTime.getHours(), curTime.getMinutes(), curTime.getSeconds(), 0)

        //
        // Based on the current date, look in the config object to see which questionnaire was active at this date
        // daysDate between questionnaireWithExtra.activatedAt and questionnaireWithExtra.retiredAt
        //
        // the RetiredAt date is not always set, and will then be manually set in the backend
        //

        const curQuestionnaire = questionnaireWithExtra.find((questionnaire) => {
          const isActive = isWithinInterval(daysDate, {
            start: new Date(questionnaire.activatedAt),
            end: new Date(questionnaire.retiredAt),
          })

          return isActive
        })

        const name = langKey ? curQuestionnaire?.questionnaire.name[langKey] : 'Language not found'
        const protocolTitle = curQuestionnaire?.protocolTitle || ''

        return (
          <div
            key={index}
            className="w-100 bg-white d-flex align-items-center py-1 px-3  "
            style={{
              borderBottom: '1px solid #e8e8e8',
              borderLeft: '2px solid #dc3545',
            }}
          >
            <ListItemContent
              label="Title:"
              title={name || '-'}
              style={{ width: '30%' }}
              capitalizeText={true}
            ></ListItemContent>

            <ListItemContent label="Protocol:" title={protocolTitle} style={{ width: '25%' }}></ListItemContent>
            <ListItemContent label="Progress:" title={'0%'} style={{ width: '15%' }}></ListItemContent>

            <div className="d-flex justify-content-end " style={{ width: '20%' }}>
              <ListItemContent
                label="Status:"
                type={'danger'}
                title={ASSIGNMENT_STATUS.MISSED}
                style={{ width: '100%' }}
                capitalizeText={true}
              ></ListItemContent>
            </div>
          </div>
        )
      })}
    </>
  )
}

type EndComponentType = {
  endDate: Date
  subjectStatus: SUBJECT_STATUS_ENUM
}

const EndComponent = ({ endDate, subjectStatus }: EndComponentType) => {
  const theEndDate = new Date(endDate)
  const date = format(theEndDate, 'dd/MM/yyyy')

  const day = format(theEndDate, 'eeee')

  return (
    <div
      className="w-100 bg-white d-flex align-items-center justify-content-center mt-2 py-5  "
      style={{
        borderBottom: '1px solid #e8e8e8',
      }}
    >
      <div className="">
        <h5 className="text-center">Participant {subjectStatus} </h5>
        <p className="text-center">
          on {day} {date} - {format(theEndDate, 'HH:mm:ss')}
        </p>
      </div>
    </div>
  )
}
